/**
 * Per-attachment caption — payload type + wire codec.
 *
 * A caption is a short text label authored alongside a media attachment
 * (image / video / voice / file). The caption travels INSIDE the signed E2EE
 * plaintext so a hostile server cannot rewrite it, detach it, move it from one
 * attachment to another, or inject a caption onto a previously caption-less
 * attachment.
 *
 * The unicode-scalar length cap bounds the serialized size, mirroring Signal's
 * behaviour. We count by `[...str].length` (code points) rather than
 * `str.length` (UTF-16 code units) so that a multi-scalar grapheme cluster
 * (e.g. a family emoji composed of ZWJ sequences) cannot be used to smuggle an
 * arbitrarily large payload past the cap. Mirrors iOS `String.unicodeScalars.count`.
 *
 * Wire format of the *plaintext that gets encrypted* (NOT the on-the-wire
 * envelope, which is the standard `kaether:e2ee:v1:` form):
 *
 *   kaether:caption:v1:<base64url(JSON)>
 *
 * JSON shape:
 *   { "schema": 1, "attachmentId": "abc",
 *     "captionText": "look at this", "ts": 1234567890 }
 *
 * Empty `captionText` is a valid "I cleared my caption" signal. Callers
 * distinguish "no caption" (no payload at all) from "explicit empty caption"
 * by absence vs presence of the payload. Mirrors
 * `kaether_for_ios/Stoat/E2EE/AttachmentCaption.swift`.
 */

// ---------------------------------------------------------------------------
// Payload
// ---------------------------------------------------------------------------

export interface AttachmentCaptionPayload {
  readonly schema: number;
  readonly attachmentId: string;
  readonly captionText: string;
  readonly ts: number; // Unix seconds; Int64 on iOS — JS number safe up to 2^53
}

export const CAPTION_SCHEMA_VERSION = 1;

/**
 * Construct an `AttachmentCaptionPayload` with the current schema version.
 */
export function makeAttachmentCaptionPayload(
  attachmentId: string,
  captionText: string,
  ts: number = Math.floor(Date.now() / 1000),
): AttachmentCaptionPayload {
  return { schema: CAPTION_SCHEMA_VERSION, attachmentId, captionText, ts };
}

// ---------------------------------------------------------------------------
// Codec errors
// ---------------------------------------------------------------------------

export type AttachmentCaptionErrorKind =
  | { tag: "malformed"; reason: string }
  | { tag: "unsupportedSchema"; version: number }
  | { tag: "captionTooLong"; unicodeScalarCount: number }
  | { tag: "emptyAttachmentId" };

export class AttachmentCaptionError extends Error {
  readonly kind: AttachmentCaptionErrorKind;

  constructor(kind: AttachmentCaptionErrorKind) {
    super(AttachmentCaptionError.message(kind));
    this.name = "AttachmentCaptionError";
    this.kind = kind;
  }

  private static message(k: AttachmentCaptionErrorKind): string {
    switch (k.tag) {
      case "malformed":
        return `malformed caption: ${k.reason}`;
      case "unsupportedSchema":
        return `unsupported caption schema: ${k.version}`;
      case "captionTooLong":
        return `caption text too long: ${k.unicodeScalarCount} unicode scalars (max ${AttachmentCaptionCodec.MAX_UNICODE_SCALAR_COUNT})`;
      case "emptyAttachmentId":
        return "attachmentId must not be empty";
    }
  }
}

// ---------------------------------------------------------------------------
// Codec
// ---------------------------------------------------------------------------

/**
 * Count Unicode scalar values (code points), NOT UTF-16 code units.
 *
 * JavaScript's `String.prototype.length` counts UTF-16 code units — a single
 * multi-scalar emoji like 🤦‍♂️ (U+1F926 U+200D U+2642 U+FE0F, 4 code points)
 * reports `str.length === 5`. Spreading via `[...str]` iterates by code point
 * so multi-scalar sequences are bounded correctly — matching iOS
 * `String.unicodeScalars.count`.
 *
 * Examples:
 *   countUnicodeScalars("a")          // 1
 *   countUnicodeScalars("🤦‍♂️")      // 4  (not 5)
 *   countUnicodeScalars("👨‍👩‍👧")   // 5  (not 1)
 */
export function countUnicodeScalars(str: string): number {
  return [...str].length;
}

export const AttachmentCaptionCodec = {
  PREFIX: "kaether:caption:v1:" as const,
  MAX_UNICODE_SCALAR_COUNT: 1500,

  /**
   * Returns true if `plaintext` begins with the caption sentinel prefix.
   */
  isCaption(plaintext: string): boolean {
    return plaintext.startsWith(AttachmentCaptionCodec.PREFIX);
  },

  /**
   * Encode an `AttachmentCaptionPayload` to `kaether:caption:v1:<base64url(JSON)>`.
   *
   * Throws `AttachmentCaptionError` if:
   * - `attachmentId` is empty → `{ tag: "emptyAttachmentId" }`
   * - `captionText` exceeds 1500 unicode scalars → `{ tag: "captionTooLong" }`
   */
  encode(payload: AttachmentCaptionPayload): string {
    if (payload.attachmentId === "") {
      throw new AttachmentCaptionError({ tag: "emptyAttachmentId" });
    }
    const scalarCount = countUnicodeScalars(payload.captionText);
    if (scalarCount > AttachmentCaptionCodec.MAX_UNICODE_SCALAR_COUNT) {
      throw new AttachmentCaptionError({
        tag: "captionTooLong",
        unicodeScalarCount: scalarCount,
      });
    }
    const json = JSON.stringify(payload);
    const b64 = base64UrlEncode(new TextEncoder().encode(json));
    return AttachmentCaptionCodec.PREFIX + b64;
  },

  /**
   * Decode a `kaether:caption:v1:` plaintext back to an `AttachmentCaptionPayload`.
   *
   * Throws:
   * - `{ tag: "malformed" }` — prefix absent, base64url broken, JSON broken,
   *   missing fields, or empty `attachmentId`.
   * - `{ tag: "unsupportedSchema" }` — `schema` field is not 1.
   * - `{ tag: "emptyAttachmentId" }` — decoded `attachmentId` is empty.
   * - `{ tag: "captionTooLong" }` — decoded `captionText` exceeds 1500 scalars
   *   (guards against maliciously-crafted ciphertexts).
   */
  decode(plaintext: string): AttachmentCaptionPayload {
    if (!plaintext.startsWith(AttachmentCaptionCodec.PREFIX)) {
      throw new AttachmentCaptionError({
        tag: "malformed",
        reason: "not a caption",
      });
    }
    const b64 = plaintext.slice(AttachmentCaptionCodec.PREFIX.length);
    let bytes: Uint8Array;
    try {
      bytes = base64UrlDecode(b64);
    } catch {
      throw new AttachmentCaptionError({
        tag: "malformed",
        reason: "invalid base64url",
      });
    }

    let raw: unknown;
    try {
      raw = JSON.parse(new TextDecoder().decode(bytes));
    } catch (e) {
      throw new AttachmentCaptionError({
        tag: "malformed",
        reason: `JSON parse failed: ${e instanceof Error ? e.message : String(e)}`,
      });
    }

    if (typeof raw !== "object" || raw === null) {
      throw new AttachmentCaptionError({
        tag: "malformed",
        reason: "not an object",
      });
    }

    const obj = raw as Record<string, unknown>;

    if (typeof obj["schema"] !== "number") {
      throw new AttachmentCaptionError({
        tag: "malformed",
        reason: "missing or non-numeric schema",
      });
    }
    if (obj["schema"] !== CAPTION_SCHEMA_VERSION) {
      throw new AttachmentCaptionError({
        tag: "unsupportedSchema",
        version: obj["schema"] as number,
      });
    }
    if (typeof obj["attachmentId"] !== "string" || obj["attachmentId"] === "") {
      throw new AttachmentCaptionError({ tag: "emptyAttachmentId" });
    }
    if (typeof obj["captionText"] !== "string") {
      throw new AttachmentCaptionError({
        tag: "malformed",
        reason: "missing or non-string captionText",
      });
    }
    if (typeof obj["ts"] !== "number") {
      throw new AttachmentCaptionError({
        tag: "malformed",
        reason: "missing or non-numeric ts",
      });
    }

    // Decode-side cap: reject maliciously-crafted oversized payloads.
    const scalarCount = countUnicodeScalars(obj["captionText"] as string);
    if (scalarCount > AttachmentCaptionCodec.MAX_UNICODE_SCALAR_COUNT) {
      throw new AttachmentCaptionError({
        tag: "captionTooLong",
        unicodeScalarCount: scalarCount,
      });
    }

    return {
      schema: obj["schema"] as number,
      attachmentId: obj["attachmentId"] as string,
      captionText: obj["captionText"] as string,
      ts: obj["ts"] as number,
    };
  },

  /**
   * Convenience: returns `null` for non-captions. Throws only for malformed
   * payloads that DID claim to be captions (i.e. had the prefix but were
   * broken inside). Mirrors iOS `AttachmentCaptionCodec.tryDecode`.
   */
  tryDecode(plaintext: string): AttachmentCaptionPayload | null {
    if (!AttachmentCaptionCodec.isCaption(plaintext)) return null;
    return AttachmentCaptionCodec.decode(plaintext);
  },
} as const;

// ---------------------------------------------------------------------------
// Base64url helpers (no external dependency)
// ---------------------------------------------------------------------------

function base64UrlEncode(data: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < data.length; i++) {
    binary += String.fromCharCode(data[i]!);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function base64UrlDecode(str: string): Uint8Array {
  const padded =
    str.replace(/-/g, "+").replace(/_/g, "/") +
    "==".slice(0, (4 - (str.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
